import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createVariantOptionCatalogEntry } from '../../services/shipturtleApi';

/**
 * Notion-style option-type picker: type to search existing variant-option-type
 * catalog entries (Size, Color, ...); if nothing matches exactly, show a
 * "Create '<name>'" row at the end of the list to define a new one.
 *
 * Selecting an existing entry calls onSelectExisting({id, name}).
 * Clicking "Create" persists a new catalog entry (get-or-create, so a
 * same-named entry created elsewhere in the meantime is reused rather than
 * duplicated) and calls onCreate({id, name, created}).
 */
function OptionNameCombobox({
  options,
  excludeIds = [],
  onSelectExisting,
  onCreate,
  placeholder = 'Search or create an option (e.g. Size, Colour)',
  disabled = false,
}) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const excludeSet = new Set(excludeIds);
  const trimmedQuery = query.trim();
  const lowerQuery = trimmedQuery.toLowerCase();

  const matches = (options || []).filter((opt) => {
    if (excludeSet.has(opt.id)) return false;
    if (!lowerQuery) return true;
    return (opt.name || '').toLowerCase().includes(lowerQuery);
  });

  const exactMatch = (options || []).some(
    (opt) => (opt.name || '').trim().toLowerCase() === lowerQuery
  );
  const canOfferCreate = trimmedQuery.length > 0 && !exactMatch;

  const highlight = (text) => {
    if (!trimmedQuery) return text;
    const lowerText = text.toLowerCase();
    const idx = lowerText.indexOf(lowerQuery);
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <mark>{text.slice(idx, idx + trimmedQuery.length)}</mark>
        {text.slice(idx + trimmedQuery.length)}
      </>
    );
  };

  const handleSelect = (opt) => {
    setQuery('');
    setIsOpen(false);
    setError('');
    onSelectExisting?.(opt);
  };

  const handleCreate = useCallback(async () => {
    if (!trimmedQuery || creating) return;
    setCreating(true);
    setError('');
    try {
      const result = await createVariantOptionCatalogEntry(trimmedQuery);
      setQuery('');
      setIsOpen(false);
      onCreate?.(result);
    } catch (e) {
      setError(typeof e?.message === 'string' ? e.message : 'Failed to create option');
    } finally {
      setCreating(false);
    }
  }, [trimmedQuery, creating, onCreate]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (matches.length > 0) {
        handleSelect(matches[0]);
      } else if (canOfferCreate) {
        handleCreate();
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div className="searchableDropdown optionNameCombobox" ref={containerRef}>
      <input
        type="text"
        className="formInput"
        value={query}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => {
          setQuery(e.target.value);
          setIsOpen(true);
          setError('');
        }}
        onFocus={() => setIsOpen(true)}
        onKeyDown={handleKeyDown}
        style={{ width: '260px' }}
      />
      {isOpen && !disabled && (
        <div className="dropdownMenu">
          {matches.length > 0 &&
            matches.slice(0, 20).map((opt) => (
              <div key={opt.id} className="dropdownItem" onClick={() => handleSelect(opt)}>
                {highlight(opt.name)}
              </div>
            ))}
          {matches.length === 0 && !canOfferCreate && (
            <div className="dropdownItem dropdownItemDisabled">
              {trimmedQuery ? 'No matching options' : 'Type to search or create an option'}
            </div>
          )}
          {canOfferCreate && (
            <div
              className="dropdownItem dropdownItemCreate"
              onClick={handleCreate}
              aria-disabled={creating}
            >
              {creating ? 'Creating…' : `+ Create "${trimmedQuery}"`}
            </div>
          )}
          {error && <div className="dropdownItem dropdownItemError">{error}</div>}
        </div>
      )}
    </div>
  );
}

export default OptionNameCombobox;
